import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import unifiedApi from '../services/unifiedApi';
import { toast } from 'sonner';
import logger from '@/utils/logger';

export const useStudents = () => {
  const queryClient = useQueryClient();

  const {
    data: students = [],
    isPending: loading,
    error: queryError,
    refetch: loadData,
  } = useQuery({
    queryKey: ['students'],
    queryFn: async () => {
      const r = await unifiedApi.students.getAllUnlimited();
      if (!r.success) throw new Error(r.error || 'Erreur lors du chargement des étudiants');
      return r.data;
    },
  });

  const error = queryError?.message ?? null;

  const [filters, setFilters] = useState({
    search: '',
    status: 'active',
    grade: '',
    hasTransport: '',
    hasAddress: '',
  });

  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [selectedStudents, setSelectedStudents] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const [showImpactModal, setShowImpactModal] = useState(false);
  const [impactAction, setImpactAction] = useState(null);
  const [selectedStudentForAction, setSelectedStudentForAction] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const filteredStudents = students.filter((student) => {
    const term = filters.search.toLowerCase();
    const matchesSearch =
      !filters.search ||
      student.first_name?.toLowerCase().includes(term) ||
      student.firstName?.toLowerCase().includes(term) ||
      student.last_name?.toLowerCase().includes(term) ||
      student.lastName?.toLowerCase().includes(term) ||
      student.ref?.toLowerCase().includes(term) ||
      student.id?.toLowerCase().includes(term) ||
      student.assignedRoute?.route_name?.toLowerCase().includes(term) ||
      student.assignedStop?.name?.toLowerCase().includes(term) ||
      student.stop_name?.toLowerCase().includes(term);
    const matchesStatus = !filters.status || student.status === filters.status;
    return matchesSearch && matchesStatus;
  });

  const handleAddStudent = () => {
    setSelectedStudent(null);
    setModalMode('create');
    setShowStudentModal(true);
  };

  const handleEditStudent = async (student) => {
    setSelectedStudent(student);
    setModalMode('edit');
    setShowStudentModal(true);
    try {
      const response = await unifiedApi.students.getById(student.id);
      if (response.success && response.data) {
        setSelectedStudent(response.data);
      }
    } catch (err) {
      logger.error('Erreur chargement détails étudiant:', err);
    }
  };

  const handleStudentSubmit = async (studentData) => {
    try {
      if (modalMode === 'edit' && selectedStudent) {
        const oldStatus = selectedStudent.status;
        const newStatus = studentData.status;
        if (oldStatus !== newStatus) {
          setSelectedStudentForAction({ ...selectedStudent, ...studentData });
          setImpactAction(newStatus === 'active' ? 'activate' : 'deactivate');
          setShowImpactModal(true);
          setShowStudentModal(false);
          return;
        }
      }

      let response;
      if (modalMode === 'create') {
        response = await unifiedApi.students.create(studentData);
      } else {
        response = await unifiedApi.students.update(selectedStudent.id, studentData);
      }

      if (response.success) {
        toast.success(modalMode === 'create' ? 'Élève créé(e) avec succès!' : 'Élève modifié(e) avec succès!');
        queryClient.invalidateQueries({ queryKey: ['students'] });
        setShowStudentModal(false);
      } else {
        throw new Error(response.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      logger.error('Erreur soumission:', err);
      toast.error(`Erreur lors de la sauvegarde: ${err.message}`);
    }
  };

  const handleDeleteStudent = (student) => {
    setSelectedStudentForAction(student);
    setImpactAction('delete');
    setShowImpactModal(true);
  };

  const _executeDeleteWithUndo = (student) => {
    queryClient.setQueryData(['students'], (old) => (old ? old.filter((s) => s.id !== student.id) : old));

    let undone = false;
    let timerId;

    const name =
      `${student.first_name || student.firstName || ''} ${student.last_name || student.lastName || ''}`.trim();

    toast(`Élève ${name} supprimé(e)`, {
      duration: 5000,
      action: {
        label: 'Annuler',
        onClick: () => {
          undone = true;
          clearTimeout(timerId);
          queryClient.setQueryData(['students'], (old) => {
            if (!old) return old;
            return old.some((s) => s.id === student.id) ? old : [...old, student];
          });
        },
      },
    });

    timerId = setTimeout(async () => {
      if (undone) return;
      try {
        const result = await unifiedApi.students.delete(student.id);
        if (!result.success) throw new Error(result.error || 'Erreur suppression');
        queryClient.invalidateQueries({ queryKey: ['students'] });
      } catch (err) {
        logger.error('Erreur suppression élève:', err);
        queryClient.invalidateQueries({ queryKey: ['students'] });
        toast.error(`Erreur lors de la suppression de l'élève`);
      }
    }, 5000);
  };

  const executeAction = async () => {
    if (!selectedStudentForAction || !impactAction) return;

    if (impactAction === 'delete') {
      const student = selectedStudentForAction;
      setShowImpactModal(false);
      setSelectedStudentForAction(null);
      setImpactAction(null);
      _executeDeleteWithUndo(student);
      return;
    }

    try {
      const newStatus = impactAction === 'activate' ? 'active' : 'inactive';
      if (selectedStudentForAction.first_name || selectedStudentForAction.firstName) {
        const { status: _status, ...rest } = selectedStudentForAction;
        const updateResponse = await unifiedApi.students.update(selectedStudentForAction.id, rest);
        if (!updateResponse.success) {
          throw new Error(updateResponse.error || "Erreur lors de la mise à jour de l'élève");
        }
      }
      const response = await unifiedApi.students.updateStatus(selectedStudentForAction.id, newStatus);

      if (response.success) {
        const actionText = impactAction === 'activate' ? 'activé(e)' : 'désactivé(e)';
        let msg = `Élève ${actionText} avec succès!`;
        if (response.impacts) {
          const { impacts } = response;
          if (impacts.parent_deactivated) msg += '\nParent désactivé (dernier enfant actif).';
          else if (impacts.parent_activated) msg += '\nParent activé.';
          if (impacts.stop_deactivated) msg += '\nArrêt désactivé (dernier élève actif).';
          else if (impacts.stop_activated) msg += '\nArrêt activé.';
          if (impacts.affected_routes_count > 0) msg += `\n${impacts.affected_routes_count} circuit(s) mis à jour.`;
        }
        toast.success(msg);
        queryClient.invalidateQueries({ queryKey: ['students'] });
        setShowImpactModal(false);
        setSelectedStudentForAction(null);
        setImpactAction(null);
      } else {
        throw new Error(response.error || "Erreur lors de l'opération");
      }
    } catch (err) {
      logger.error('Erreur opération:', err);
      toast.error(`Erreur lors de l'opération: ${err.message}`);
    }
  };

  const handleImportClick = () => setShowImportModal(true);

  const handleImportComplete = (results) => {
    setImportResults(results);
    setShowImportModal(false);
    queryClient.invalidateQueries({ queryKey: ['students'] });
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedStudents.length === 0) return;

    if (bulkAction === 'delete') {
      setShowBulkDeleteModal(true);
      return;
    }

    setBulkLoading(true);
    try {
      const count = selectedStudents.length;
      const newStatus = bulkAction === 'activate' ? 'active' : 'inactive';
      await Promise.all(selectedStudents.map((id) => unifiedApi.students.updateStatus(id, newStatus)));
      const label = bulkAction === 'activate' ? 'activé(s)' : 'désactivé(s)';
      toast.success(`${count} élève(s) ${label} avec succès`);
      setSelectedStudents([]);
      setBulkAction('');
      queryClient.invalidateQueries({ queryKey: ['students'] });
    } catch (err) {
      logger.error('Erreur action en lot:', err);
      toast.error("Erreur lors de l'action en lot");
    } finally {
      setBulkLoading(false);
    }
  };

  const confirmBulkDelete = () => {
    const studentsToDelete = students.filter((s) => selectedStudents.includes(s.id));
    const count = studentsToDelete.length;
    setShowBulkDeleteModal(false);
    setSelectedStudents([]);
    setBulkAction('');

    queryClient.setQueryData(['students'], (old) => (old ? old.filter((s) => !selectedStudents.includes(s.id)) : old));

    let undone = false;
    let timerId;

    toast(`${count} élève(s) supprimé(s)`, {
      duration: 5000,
      action: {
        label: 'Annuler',
        onClick: () => {
          undone = true;
          clearTimeout(timerId);
          queryClient.setQueryData(['students'], (old) => {
            if (!old) return old;
            const existingIds = new Set(old.map((s) => s.id));
            return [...old, ...studentsToDelete.filter((s) => !existingIds.has(s.id))];
          });
          setSelectedStudents(studentsToDelete.map((s) => s.id));
        },
      },
    });

    timerId = setTimeout(async () => {
      if (undone) return;
      try {
        await Promise.all(studentsToDelete.map((s) => unifiedApi.students.delete(s.id)));
        queryClient.invalidateQueries({ queryKey: ['students'] });
      } catch (err) {
        logger.error('Erreur suppression en lot:', err);
        queryClient.invalidateQueries({ queryKey: ['students'] });
        toast.error('Erreur lors de la suppression en lot');
      }
    }, 5000);
  };

  const cancelBulkDeleteModal = () => {
    setShowBulkDeleteModal(false);
  };

  const toggleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map((s) => s.id));
    }
  };

  const toggleSelectStudent = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId],
    );
  };

  const cancelImpactModal = () => {
    setShowImpactModal(false);
    setSelectedStudentForAction(null);
    setImpactAction(null);
  };

  return {
    students,
    filteredStudents,
    loading,
    error,
    filters,
    setFilters,
    showStudentModal,
    setShowStudentModal,
    selectedStudent,
    modalMode,
    selectedStudents,
    setSelectedStudents,
    bulkAction,
    setBulkAction,
    bulkLoading,
    showImportModal,
    setShowImportModal,
    importResults,
    showImpactModal,
    impactAction,
    selectedStudentForAction,
    loadData,
    handleAddStudent,
    handleEditStudent,
    handleStudentSubmit,
    handleDeleteStudent,
    executeAction,
    handleImportClick,
    handleImportComplete,
    handleBulkAction,
    confirmBulkDelete,
    cancelBulkDeleteModal,
    showBulkDeleteModal,
    toggleSelectAll,
    toggleSelectStudent,
    cancelImpactModal,
  };
};

export default useStudents;
